import { useState, useEffect } from 'react';
import { useStoreState } from 'easy-peasy';
import { isEqual, omit } from 'lodash';
import uuidv4 from 'uuid/v4';
import { toast } from 'react-toastify';
import swal from 'sweetalert';
import client from '../../../../utils/apolloClient';
import {
  fetchCompanyQuery,
  createCompanyMutation,
  updateCompanyMutation,
} from '../services/companySummaryQueries';

// ─── ABN validation (Australian checksum algorithm) ──────────────────────────

export const isValidABN = abn => {
  const digits = String(abn).replace(/\s/g, '');
  if (!/^\d{11}$/.test(digits)) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const d = digits.split('').map(Number);
  d[0] -= 1;
  return weights.reduce((acc, w, i) => acc + w * d[i], 0) % 89 === 0;
};

// ─── BSB formatter ────────────────────────────────────────────────────────────

export const formatBSB = val => {
  let v = val.replace(/[^0-9]/g, '');
  if (v.length > 3) v = v.substr(0, 3) + '-' + v.substr(3, 6);
  return v;
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const removeTypename = arr =>
  (arr || []).map(item => omit(item, ['__typename']));

const isBankDetailEmpty = bank_detail =>
  Object.values(omit(bank_detail, ['__typename'])).every(v => v === '');

const isConfigEmpty = config =>
  Object.values(omit(config, ['__typename']))
    .filter(v => typeof v === 'string')
    .every(v => v === '');

const buildContactArray = fields => {
  const makePrimary = (type, value, i) =>
    value
      ? [{ type, value, display: true, is_primary: true, display_order: i }]
      : [];

  return removeTypename([
    ...makePrimary('email', fields.primary_contact_email, 0),
    ...makePrimary('mobile', fields.primary_contact_phone, 1),
    ...makePrimary('website', fields.business_site, 2),
  ]);
};

export const buildCreateInput = (fields, userId) => ({
  user_id: userId,
  company_id: fields.company_id,
  name: fields.name,
  status: fields.status,
  abn: fields.registration_id,
  address_line_1: fields.address_line_1,
  address_line_2: fields.address_line_2,
  city: fields.city,
  state: fields.state,
  country: fields.country,
  post_code: fields.post_code,
  social: [],
  contact: buildContactArray(fields),
  tax: fields.tax,
  ...(!isBankDetailEmpty(fields.bank_detail) && { bank_detail: omit(fields.bank_detail, ['__typename']) }),
  ...(!isConfigEmpty(fields.kravein_configuration) && { kravein_configuration: omit(fields.kravein_configuration, ['__typename']) }),
});

export const buildUpdateInput = (fields, originalCompany, userId) => {
  const input = {
    user_id: userId,
    company_id: fields.company_id,
    abn: fields.registration_id,
    contact: buildContactArray(fields),
    tax: fields.tax,
    ...(!isBankDetailEmpty(fields.bank_detail) && { bank_detail: omit(fields.bank_detail, ['__typename']) }),
    kravein_configuration: omit(fields.kravein_configuration, ['__typename']),
  };

  const DIFF_KEYS = [
    'name',
    'status',
    'address_line_1',
    'address_line_2',
    'city',
    'state',
    'country',
    'post_code',
  ];

  DIFF_KEYS.forEach(key => {
    const original = originalCompany[key] || '';
    if (!isEqual(original, fields[key])) {
      input[key] = fields[key];
    }
  });

  return input;
};

export const validate = fields => {
  const errs = {};
  if (!fields.name.trim()) errs.name = 'Company name is required';
  if (!fields.status) errs.status = 'Status is required';
  if (!fields.address_line_1.trim())
    errs.address_line_1 = 'Street address is required';
  if (!fields.city.trim()) errs.city = 'Suburb / City is required';
  if (!fields.state) errs.state = 'State is required';
  if (!fields.post_code.trim()) errs.post_code = 'Post code is required';
  if (!fields.country.trim()) errs.country = 'Country is required';
  // Optional fields — validate format only when a value is present
  if (
    fields.primary_contact_email &&
    !/\S+@\S+\.\S+/.test(fields.primary_contact_email)
  ) {
    errs.primary_contact_email = 'Invalid email address';
  }
  if (fields.registration_id) {
    const digits = fields.registration_id.replace(/\s/g, '');
    if (digits && !isValidABN(digits))
      errs.registration_id = 'Invalid ABN — check and re-enter';
  }
  return errs;
};

// ─── Form defaults ────────────────────────────────────────────────────────────

export const INITIAL_BANK_DETAIL = {
  account_name: '',
  account_number: '',
  bank_name: '',
  branch_name: '',
  ifsc_code: '',
  payment_method: '',
};

export const INITIAL_KRAVEIN_CONFIG = {
  auto_send_invoice: true,
  invoice_cycle: 'Weekly',
  invoice_email: '',
};

export const INITIAL_FORM = {
  company_id: '',
  name: '',
  status: 'ACTIVE',
  registration_id: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  country: 'AU',
  post_code: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  business_site: '',
  tax: [],
  bank_detail: { ...INITIAL_BANK_DETAIL },
  kravein_configuration: { ...INITIAL_KRAVEIN_CONFIG },
};

export const mapCompanyToForm = company => {
  if (!company) return null;
  const contacts = company.contact || [];
  const primaryEmail = contacts.find(c => c.is_primary && c.type === 'email');
  const primaryMobile = contacts.find(c => c.is_primary && c.type === 'mobile');
  const businessSite = contacts.find(c => c.is_primary && c.type === 'website');

  return {
    company_id: company.company_id || '',
    name: company.name || '',
    status: company.status || 'ACTIVE',
    registration_id: company.registration_id || company.abn || '',
    address_line_1: company.address_line_1 || '',
    address_line_2: company.address_line_2 || '',
    city: company.city || '',
    state: company.state || '',
    country: company.country || 'AU',
    post_code: company.post_code || '',
    primary_contact_email: primaryEmail ? primaryEmail.value : '',
    primary_contact_phone: primaryMobile ? primaryMobile.value : '',
    business_site: businessSite ? businessSite.value : '',
    tax: removeTypename(company.tax),
    bank_detail: company.bank_detail
      ? omit(company.bank_detail, ['__typename'])
      : { ...INITIAL_BANK_DETAIL },
    kravein_configuration: company.kravein_configuration
      ? omit(company.kravein_configuration, ['__typename'])
      : { ...INITIAL_KRAVEIN_CONFIG },
  };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useCompanyForm = ({ companyId, onSuccess }) => {
  const { userId } = useStoreState(state => state.auth);
  const isEdit = !!companyId;

  const [fetchedCompany, setFetchedCompany] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [fetchError, setFetchError] = useState(null);

  const [fields, setFieldsState] = useState(() => ({
    ...INITIAL_FORM,
    company_id: uuidv4(),
  }));
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [abnStatus, setAbnStatus] = useState('idle');

  useEffect(() => {
    if (!companyId || !userId) return;
    setFetchLoading(true);
    setFetchError(null);
    client.clientPrivate
      .query({
        query: fetchCompanyQuery,
        variables: { companyId },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const company = data.fetch_company;
        if (company) {
          setFetchedCompany(company);
          setFieldsState(mapCompanyToForm(company));
          const abn = company.registration_id || '';
          if (abn) {
            setAbnStatus(
              isValidABN(abn.replace(/\s/g, '')) ? 'valid' : 'invalid',
            );
          }
        }
        setFetchLoading(false);
      })
      .catch(err => {
        setFetchError(err);
        setFetchLoading(false);
      });
  }, [companyId, userId]);

  const setField = (key, value) => {
    setFieldsState(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const handleABNChange = value => {
    setField('registration_id', value);
    const digits = value.replace(/\s/g, '');
    if (!digits || digits.length < 11) {
      setAbnStatus('idle');
      return;
    }
    setAbnStatus(isValidABN(digits) ? 'valid' : 'invalid');
  };

  const submit = async () => {
    const errs = validate(fields);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitLoading(true);
    try {
      if (isEdit) {
        const input = buildUpdateInput(fields, fetchedCompany, userId);
        const { data } = await client.clientPrivate.mutate({
          mutation: updateCompanyMutation,
          variables: { input },
        });
        const result = data.update_company;
        if (result.error && result.error.length > 0) {
          result.error.forEach(err => toast.error(err.description));
        } else {
          await swal(
            'Nice!',
            'Company has been updated successfully',
            'success',
          );
          if (onSuccess) onSuccess();
        }
      } else {
        const input = buildCreateInput(fields, userId);
        const { data } = await client.clientPrivate.mutate({
          mutation: createCompanyMutation,
          variables: { input },
        });
        const result = data.create_company;
        if (result.error && result.error.length > 0) {
          result.error.forEach(err => toast.error(err.description));
        } else {
          await swal(
            'Great!',
            'Company has been created successfully!',
            'success',
          );
          if (onSuccess) onSuccess();
        }
      }
    } catch (err) {
      toast.error(err.message || 'An unexpected error occurred');
    } finally {
      setSubmitLoading(false);
    }
  };

  return {
    fields,
    errors,
    fetchLoading,
    fetchError,
    submitLoading,
    abnStatus,
    isEdit,
    setField,
    handleABNChange,
    submit,
  };
};

export default useCompanyForm;
